import React, { useMemo, useState, useCallback } from 'react';
import { Container, Paper, Title, Text, Group, Stack as MantineStack, Badge, Box, Button } from '@mantine/core';
import { Game, Player, Stack as GameStack, PlayerQueue } from '../../types/models';
import PlayerStack from '../PlayerStack';
import QueueVisualization from '../QueueVisualization';
import PushNotificationPrompt from '../PushNotificationPrompt';

interface QueueLobbyProps {
  game: Game;
  currentPlayer: Player;
  playerQueues: PlayerQueue[];
  activeStack?: GameStack | null;
  onResumeActiveStack?: () => void;
  onStackPickup?: (stack: GameStack) => Promise<void> | void;
}

interface PlayerQueueStatus {
  player: Player;
  queue: GameStack[];
  activeStack?: GameStack;
  status: 'waiting' | 'active' | 'completed';
  queueSize: number;
}

export const QueueLobby: React.FC<QueueLobbyProps> = ({
  game,
  currentPlayer,
  playerQueues,
  activeStack,
  onResumeActiveStack,
  onStackPickup
}) => {
  const activeStackOriginPlayer = activeStack
    ? game.players.find(player => player.id === activeStack.originPlayerId)
    : null;
  
  // Calculate player queue statuses
  const { playerStatuses, globalStatus } = useMemo(() => {
    const statuses: PlayerQueueStatus[] = [];
    let totalActiveWork = 0;
    let totalPendingWork = 0;
    
    for (const player of game.players) {
      // Find this player's queue
      const playerQueue = playerQueues.find(q => q.playerId === player.id);
      // Resolve stack IDs to actual stack objects
      let queue = playerQueue?.stackIds
        .map(stackId => game.stacks.find(s => s.id === stackId))
        .filter((stack): stack is GameStack => stack !== undefined) || [];
      
      // Find any active stack for this player
      const activeStack = game.stacks.find(s => 
        s.activePlayerId === player.id && s.status === 'active'
      );
      
      let status: 'waiting' | 'active' | 'completed' = 'waiting';

      // An empty queue only means "nothing to do right now" — the player is
      // done when no stack still expects an entry from them.
      const hasRemainingTurns = game.stacks.some(s =>
        s.expectedEntries?.some(
          entry => String(entry.expectedPlayerId) === String(player.id) && !entry.completed
        )
      );

      if (activeStack) {
        status = 'active';
        totalActiveWork++;
      } else if (queue.length === 0 && !hasRemainingTurns) {
        status = 'completed';
      } else {
        totalPendingWork += queue.length;
      }
      
      if (activeStack && queue.some(stack => stack.id === activeStack.id)) {
        queue = [activeStack, ...queue.filter(stack => stack.id !== activeStack.id)];
      }

      statuses.push({
        player,
        queue,
        activeStack,
        status,
        queueSize: queue.length
      });
    }
    
    // Generate global status message
    let globalMessage = "Game in progress...";
    
    if (totalActiveWork > 1) {
      globalMessage = `${totalActiveWork} players are working...`;
    } else if (totalActiveWork === 1) {
      const activePlayer = statuses.find(s => s.status === 'active')?.player;
      if (activePlayer) {
        globalMessage = `Waiting for ${activePlayer.name} to finish...`;
      }
    } else if (totalPendingWork === 0) {
      globalMessage = "All turns complete - waiting for game to update...";
    } else {
      globalMessage = `${totalPendingWork} turn${totalPendingWork !== 1 ? 's' : ''} remaining`;
    }
    
    return {
      playerStatuses: statuses,
      globalStatus: globalMessage
    };
  }, [game, playerQueues]);

  // Helper to get status badge
  const getStatusBadge = (playerStatus: PlayerQueueStatus): { text: string; color: string } => {
    const isCurrentUser = playerStatus.player.id === currentPlayer.id;
    
    switch (playerStatus.status) {
      case 'active':
        return { text: 'BUSY!', color: 'yellow' };
      case 'completed':
        return { text: 'DONE', color: 'green' };
      case 'waiting':
        if (playerStatus.queueSize === 1) {
          return { 
            text: isCurrentUser ? 'READY!' : '1 left', 
            color: isCurrentUser ? 'red' : 'blue' 
          };
        } else if (playerStatus.queueSize > 1) {
          return { 
            text: isCurrentUser ? 'READY!' : `${playerStatus.queueSize} left`, 
            color: isCurrentUser ? 'red' : 'blue' 
          };
        } else {
          return { text: 'waiting', color: 'gray' };
        }
      default:
        return { text: 'waiting', color: 'gray' };
    }
  };

  // Track loading state for stack pickup
  const [isPickingUp, setIsPickingUp] = useState(false);

  // Handle stack pickup with loading state
  const handleStackPickup = useCallback(async (stack: GameStack) => {
    if (!onStackPickup || isPickingUp) return;

    setIsPickingUp(true);
    try {
      await onStackPickup(stack);
    } finally {
      setIsPickingUp(false);
    }
  }, [onStackPickup, isPickingUp]);

  return (
    <Container size="md" py="xl">
      <style>
        {`
          @keyframes throb {
            0% { transform: scale(1.15); }
            50% { transform: scale(1.25); }
            100% { transform: scale(1.15); }
          }
        `}
      </style>
      <Paper p="xl" radius="md" shadow="sm">
        <MantineStack gap="xl" align="center">
          {/* Header */}
          <MantineStack gap="sm" align="center">
            <Title order={2} ta="center">Game in Progress</Title>
            <Text size="lg" c="dimmed" ta="center">
              {globalStatus}
            </Text>
            {activeStack && onResumeActiveStack && (
              <MantineStack gap="xs" align="center">
                <Text size="sm" c="dimmed" ta="center">
                  You're holding {activeStackOriginPlayer?.name || 'someone'}'s stack.
                </Text>
                <Button variant="light" onClick={onResumeActiveStack}>
                  Resume your turn
                </Button>
              </MantineStack>
            )}
          </MantineStack>

          {/* Turn notifications */}
          <PushNotificationPrompt gameKey={game.key} />

          {/* Player Queue Grid */}
          <Box w="100%" maw={700}>
            <Group justify="center" gap="xl" align="start">
              {playerStatuses
                .sort((a, b) => {
                  // Sort current player first
                  if (currentPlayer) {
                    if (a.player.id === currentPlayer.id) return -1;
                    if (b.player.id === currentPlayer.id) return 1;
                  }
                  return 0;
                })
                .map((playerStatus) => {
                const badge = getStatusBadge(playerStatus);
                const isCurrentUser = playerStatus.player.id === currentPlayer.id;
                const hasQueue = playerStatus.queueSize > 0;
                const isReady = isCurrentUser && hasQueue && playerStatus.status === 'waiting';
                const canClick = isReady && !isPickingUp;
                
                return (
                  <MantineStack 
                    key={playerStatus.player.id} 
                    gap="sm" 
                    align="center"
                    style={{
                      opacity: playerStatus.status === 'active' ? 1 : 0.85,
                      transform: isReady ? 'scale(1.15)' : playerStatus.status === 'active' ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      animation: isReady ? 'throb 1.5s ease-in-out infinite' : 'none',
                    }}
                  >
                    {/* Player Polaroid */}
                    <PlayerStack
                      numCards={game.players.length}
                      avatar={playerStatus.player.avatarUrl}
                      name={isCurrentUser ? 'You' : playerStatus.player.name}
                      handwritingFont={playerStatus.player.handwritingFont}
                      onClick={canClick ? () => {
                        const topStack = playerStatus.queue[0];
                        if (topStack) {
                          handleStackPickup(topStack);
                        }
                      } : undefined}
                    />

                    {/* Status Badge */}
                    <Badge
                      variant="filled"
                      color={isCurrentUser && isPickingUp ? 'gray' : badge.color}
                      size="md"
                      style={{
                        textTransform: 'uppercase',
                        fontSize: '11px',
                        fontWeight: 700,
                        borderRadius: '12px',
                        padding: '4px 12px',
                      }}
                    >
                      {isCurrentUser && isPickingUp ? 'LOADING...' : badge.text}
                    </Badge>

                    {/* Queue Visualization */}
                    <QueueVisualization
                      queue={playerStatus.queue}
                      activeStackId={playerStatus.activeStack?.id}
                      onStackClick={canClick ? handleStackPickup : undefined}
                      isCurrentUser={isCurrentUser}
                      players={game.players}
                    />
                  </MantineStack>
                );
              })}
            </Group>
          </Box>

          {/* Helpful Message for Current Player */}
          {currentPlayer && (
            <Box>
              {playerStatuses.find(ps => ps.player.id === currentPlayer.id)?.status === 'completed' ? (
                <Text size="sm" c="green" ta="center" fw={500}>
                  🎉 You've completed all your work! Waiting for others to finish.
                </Text>
              ) : playerStatuses.find(ps => ps.player.id === currentPlayer.id)?.queueSize > 0 ? (
                <Text size="sm" c="blue" ta="center">
                  Click the top avatar in your queue to start working!
                </Text>
              ) : (
                <Text size="sm" c="blue" ta="center">
                  Hang tight! We'll let you know when it's your turn.
                </Text>
              )}
            </Box>
          )}
        </MantineStack>
      </Paper>
    </Container>
  );
};
